import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, Type, Briefcase, Settings, Plus, Trash2, Edit, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';

const TemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tempFields, setTempFields] = useState({ photo: {}, name: {}, title: {} });
  const [previewScale, setPreviewScale] = useState(1);
  const [guides, setGuides] = useState({ horizontal: [], vertical: [] });
  const [activeElement, setActiveElement] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [resizing, setResizing] = useState(null);

  const previewRef = useRef(null);
  const SNAP_THRESHOLD = 10;

  useEffect(() => {
    const saved = localStorage.getItem('templates');
    if (saved) {
      const parsed = JSON.parse(saved);
      setTemplates(parsed);
      const found = parsed.find(t => t.id === Number(templateId));
      if (found) {
        setEditingTemplate(found);
        setTempFields(found.fields);
      } else {
        alert('Không tìm thấy mẫu thiệp!');
        navigate('/admin');
      }
    }
  }, [templateId, navigate]);

  // === HELPER: Get element bounds ===
  const getElementBounds = (element, elementType) => {
    if (elementType === 'photo') {
      return {
        left: element.x,
        right: element.x + element.width,
        top: element.y,
        bottom: element.y + element.height,
        centerX: element.x + element.width / 2,
        centerY: element.y + element.height / 2,
        width: element.width,
        height: element.height
      };
    } else {
      const width = 0;
      const height = 0;
      return {
        left: element.x,
        right: element.x,
        top: element.y,
        bottom: element.y,
        centerX: element.x,
        centerY: element.y,
        width: width,
        height: height
      };
    }
  };

  // === DRAG & DROP LOGIC ===
  const handleMouseDown = (e, elementType, action = 'drag') => {
    e.preventDefault();
    e.stopPropagation();

    const element = tempFields[elementType];
    const previewRect = previewRef.current.getBoundingClientRect();

    const mouseXInPreview = e.clientX - previewRect.left;
    const mouseYInPreview = e.clientY - previewRect.top;

    if (action === 'drag') {
      let offsetX, offsetY;

      if (elementType === 'photo') {
        offsetX = mouseXInPreview - (element.x * previewScale);
        offsetY = mouseYInPreview - (element.y * previewScale);
      } else {
        offsetX = mouseXInPreview - (element.x * previewScale);
        offsetY = mouseYInPreview - (element.y * previewScale);
      }

      setDragging({
        type: elementType,
        offsetX,
        offsetY,
        initialWidth: element.width,
        initialHeight: element.height
      });
    } else if (action === 'resize') {
      setResizing({
        type: elementType,
        startX: e.clientX,
        startY: e.clientY,
        initialWidth: element.width || 300,
        initialHeight: element.height || 300,
        initialX: element.x,
        initialY: element.y
      });
    }

    setActiveElement(elementType);
  };

  const handleMouseMove = (e) => {
    if (dragging) {
      const previewRect = previewRef.current.getBoundingClientRect();
      const mouseXInPreview = e.clientX - previewRect.left;
      const mouseYInPreview = e.clientY - previewRect.top;

      let newX, newY;

      if (dragging.type === 'photo') {
        newX = (mouseXInPreview - dragging.offsetX) / previewScale;
        newY = (mouseYInPreview - dragging.offsetY) / previewScale;
      } else {
        newX = (mouseXInPreview - dragging.offsetX) / previewScale;
        newY = (mouseYInPreview - dragging.offsetY) / previewScale;
      }

      const newElement = {
        ...tempFields[dragging.type],
        x: newX,
        y: newY
      };

      const newGuides = calculateGuides(
        newElement,
        dragging.type,
        editingTemplate.width,
        editingTemplate.height,
        tempFields
      );
      setGuides(newGuides);

      setTempFields(prev => ({
        ...prev,
        [dragging.type]: newElement
      }));
    } else if (resizing) {
      const deltaX = (e.clientX - resizing.startX) / previewScale;
      const deltaY = (e.clientY - resizing.startY) / previewScale;

      const newWidth = Math.max(100, resizing.initialWidth + deltaX);
      const newHeight = Math.max(100, resizing.initialHeight + deltaY);

      setTempFields(prev => ({
        ...prev,
        [resizing.type]: {
          ...prev[resizing.type],
          width: newWidth,
          height: newHeight
        }
      }));
    }
  };

  const handleMouseUp = () => {
    if (dragging) {
      let element = tempFields[dragging.type];
      element = snapPosition(
        element,
        dragging.type,
        editingTemplate.width,
        editingTemplate.height,
        tempFields
      );

      setTempFields(prev => ({
        ...prev,
        [dragging.type]: element
      }));

      setGuides({ horizontal: [], vertical: [] });
      setDragging(null);
    } else if (resizing) {
      setResizing(null);
    }
  };

  useEffect(() => {
    if (dragging || resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, resizing, previewScale, tempFields, editingTemplate]);

  // === SMART GUIDES & SNAP LOGIC ===
  const calculateGuides = (element, elementType, templateWidth, templateHeight, allFields) => {
    const centerX = templateWidth / 2;
    const centerY = templateHeight / 2;

    const guides = { horizontal: [], vertical: [] };
    const currentBounds = getElementBounds(element, elementType);

    if (Math.abs(currentBounds.centerX - centerX) < SNAP_THRESHOLD) {
      guides.vertical.push({ position: centerX, type: 'center', color: 'blue' });
    }

    if (elementType === 'photo') {
      if (Math.abs(currentBounds.left) < SNAP_THRESHOLD) {
        guides.vertical.push({ position: 0, type: 'left edge', color: 'blue' });
      }
      if (Math.abs(currentBounds.right - templateWidth) < SNAP_THRESHOLD) {
        guides.vertical.push({ position: templateWidth, type: 'right edge', color: 'blue' });
      }
    }

    if (Math.abs(currentBounds.centerY - centerY) < SNAP_THRESHOLD) {
      guides.horizontal.push({ position: centerY, type: 'middle', color: 'blue' });
    }

    if (elementType === 'photo') {
      if (Math.abs(currentBounds.top) < SNAP_THRESHOLD) {
        guides.horizontal.push({ position: 0, type: 'top edge', color: 'blue' });
      }
      if (Math.abs(currentBounds.bottom - templateHeight) < SNAP_THRESHOLD) {
        guides.horizontal.push({ position: templateHeight, type: 'bottom edge', color: 'blue' });
      }
    }

    Object.keys(allFields).forEach(otherType => {
      if (otherType === elementType) return;

      const otherElement = allFields[otherType];
      if (!otherElement || !otherElement.x) return;

      const otherBounds = getElementBounds(otherElement, otherType);

      if (Math.abs(currentBounds.centerX - otherBounds.centerX) < SNAP_THRESHOLD) {
        guides.vertical.push({
          position: otherBounds.centerX,
          type: `align with ${otherType}`,
          color: 'purple'
        });
      }

      if (Math.abs(currentBounds.centerY - otherBounds.centerY) < SNAP_THRESHOLD) {
        guides.horizontal.push({
          position: otherBounds.centerY,
          type: `align with ${otherType}`,
          color: 'purple'
        });
      }
    });

    return guides;
  };

  const snapPosition = (element, elementType, templateWidth, templateHeight, allFields) => {
    const centerX = templateWidth / 2;
    const centerY = templateHeight / 2;

    let snappedElement = { ...element };

    if (elementType === 'photo') {
      const currentBounds = getElementBounds(element, elementType);

      if (Math.abs(currentBounds.centerX - centerX) < SNAP_THRESHOLD) {
        snappedElement.x = centerX - element.width / 2;
      }
      if (Math.abs(currentBounds.centerY - centerY) < SNAP_THRESHOLD) {
        snappedElement.y = centerY - element.height / 2;
      }

      if (Math.abs(element.x) < SNAP_THRESHOLD) {
        snappedElement.x = 0;
      }
      if (Math.abs(element.x + element.width - templateWidth) < SNAP_THRESHOLD) {
        snappedElement.x = templateWidth - element.width;
      }
      if (Math.abs(element.y) < SNAP_THRESHOLD) {
        snappedElement.y = 0;
      }
      if (Math.abs(element.y + element.height - templateHeight) < SNAP_THRESHOLD) {
        snappedElement.y = templateHeight - element.height;
      }

      Object.keys(allFields).forEach(otherType => {
        if (otherType === elementType) return;

        const otherElement = allFields[otherType];
        if (!otherElement || !otherElement.x) return;

        const otherBounds = getElementBounds(otherElement, otherType);
        const newBounds = getElementBounds(snappedElement, elementType);

        if (Math.abs(newBounds.centerX - otherBounds.centerX) < SNAP_THRESHOLD) {
          snappedElement.x = otherBounds.centerX - snappedElement.width / 2;
        }
        if (Math.abs(newBounds.centerY - otherBounds.centerY) < SNAP_THRESHOLD) {
          snappedElement.y = otherBounds.centerY - snappedElement.height / 2;
        }
      });
    } else {
      if (Math.abs(element.x - centerX) < SNAP_THRESHOLD) {
        snappedElement.x = centerX;
      }
      if (Math.abs(element.y - centerY) < SNAP_THRESHOLD) {
        snappedElement.y = centerY;
      }

      Object.keys(allFields).forEach(otherType => {
        if (otherType === elementType) return;

        const otherElement = allFields[otherType];
        if (!otherElement || !otherElement.x) return;

        const otherBounds = getElementBounds(otherElement, otherType);

        if (Math.abs(element.x - otherBounds.centerX) < SNAP_THRESHOLD) {
          snappedElement.x = otherBounds.centerX;
        }
        if (Math.abs(element.y - otherBounds.centerY) < SNAP_THRESHOLD) {
          snappedElement.y = otherBounds.centerY;
        }
      });
    }

    return snappedElement;
  };

  // === ALIGNMENT FUNCTIONS ===
  const alignElement = (elementType, alignment) => {
    if (!editingTemplate) return;

    const templateWidth = editingTemplate.width;
    const templateHeight = editingTemplate.height;
    const element = tempFields[elementType];

    let newElement = { ...element };

    if (elementType === 'photo') {
      switch (alignment) {
        case 'left':
          newElement.x = 0;
          break;
        case 'center-h':
          newElement.x = (templateWidth - element.width) / 2;
          break;
        case 'right':
          newElement.x = templateWidth - element.width;
          break;
        case 'top':
          newElement.y = 0;
          break;
        case 'center-v':
          newElement.y = (templateHeight - element.height) / 2;
          break;
        case 'bottom':
          newElement.y = templateHeight - element.height;
          break;
      }
    } else {
      switch (alignment) {
        case 'left':
          newElement.x = 0;
          break;
        case 'center-h':
          newElement.x = templateWidth / 2;
          break;
        case 'right':
          newElement.x = templateWidth;
          break;
        case 'top':
          newElement.y = 0;
          break;
        case 'center-v':
          newElement.y = templateHeight / 2;
          break;
        case 'bottom':
          newElement.y = templateHeight;
          break;
      }
    }

    setTempFields(prev => ({
      ...prev,
      [elementType]: newElement
    }));
  };

  const saveEditedTemplate = () => {
    const updatedTemplates = templates.map(t =>
      t.id === editingTemplate.id ? { ...editingTemplate, fields: tempFields } : t
    );
    setTemplates(updatedTemplates);
    localStorage.setItem('templates', JSON.stringify(updatedTemplates));
    alert('Đã lưu mẫu thành công!');
    navigate('/admin');
  };

  if (!editingTemplate) {
    return <div className="min-h-screen flex items-center justify-center text-xl">Đang tải mẫu...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 overflow-auto">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Edit className="w-8 h-8 text-blue-600" />
            Chỉnh sửa mẫu: {editingTemplate.name}
          </h1>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition"
            >
              Hủy
            </button>
            <button
              onClick={saveEditedTemplate}
              className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-bold transition"
            >
              Lưu mẫu
            </button>
          </div>
        </div>

        {activeElement && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
            <div className="flex items-center gap-4">
              <span className="font-bold text-gray-700">
                Căn chỉnh {activeElement === 'photo' ? 'Ảnh' : activeElement === 'name' ? 'Tên' : 'Chức vụ'}:
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => alignElement(activeElement, 'left')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn trái"
                >
                  <AlignLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => alignElement(activeElement, 'center-h')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn giữa ngang"
                >
                  <AlignCenter className="w-5 h-5" />
                </button>
                <button
                  onClick={() => alignElement(activeElement, 'right')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn phải"
                >
                  <AlignRight className="w-5 h-5" />
                </button>
                <div className="w-px bg-gray-300 mx-2"></div>
                <button
                  onClick={() => alignElement(activeElement, 'top')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn trên"
                >
                  <div className="w-5 h-5 border-t-2 border-gray-700"></div>
                </button>
                <button
                  onClick={() => alignElement(activeElement, 'center-v')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn giữa dọc"
                >
                  <div className="w-5 h-5 border-t border-gray-700 flex items-center">
                    <div className="w-full border-t-2 border-gray-700"></div>
                  </div>
                </button>
                <button
                  onClick={() => alignElement(activeElement, 'bottom')}
                  className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition"
                  title="Căn dưới"
                >
                  <div className="w-5 h-5 border-b-2 border-gray-700"></div>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-8 flex items-center gap-2">
            <Settings className="w-6 h-6 text-indigo-600" />
            Thiết lập thuộc tính cho thiệp
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-5 border-r border-gray-200 pr-6">
              <h3 className="text-xl font-bold text-blue-700">Ảnh đại diện</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cách hiển thị ảnh</label>
                <select
                  value={tempFields.photo?.fit || 'cover'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, photo: { ...prev.photo, fit: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cover">Cover (Fill khung - cắt nếu cần)</option>
                  <option value="contain">Contain (Hiện toàn bộ ảnh)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hình dạng</label>
                <select
                  value={tempFields.photo?.shape || 'circle'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, photo: { ...prev.photo, shape: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="circle">Tròn (circle)</option>
                  <option value="square">Vuông</option>
                  <option value="rounded">Bo góc</option>
                </select>
              </div>
              {tempFields.photo?.shape === 'rounded' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bán kính bo góc (px)</label>
                  <input
                    type="number"
                    value={tempFields.photo?.borderRadius || 20}
                    onChange={(e) => setTempFields((prev) => ({ ...prev, photo: { ...prev.photo, borderRadius: Number(e.target.value) } }))}
                    min={0}
                    max={200}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chiều rộng (px)</label>
                  <input
                    type="number"
                    value={tempFields.photo?.width || 300}
                    onChange={(e) => setTempFields((prev) => ({ ...prev, photo: { ...prev.photo, width: Number(e.target.value) } }))}
                    min={100}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Chiều cao (px)</label>
                  <input
                    type="number"
                    value={tempFields.photo?.height || 300}
                    onChange={(e) => setTempFields((prev) => ({ ...prev, photo: { ...prev.photo, height: Number(e.target.value) } }))}
                    min={100}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-5 border-r border-gray-200 pr-6">
              <h3 className="text-xl font-bold text-red-700">Họ và tên</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cỡ chữ (px)</label>
                <input
                  type="number"
                  value={tempFields.name?.fontSize || 60}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, fontSize: Number(e.target.value) } }))}
                  min={20}
                  max={150}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Độ đậm</label>
                <select
                  value={tempFields.name?.fontWeight || 'bold'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, fontWeight: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="normal">Normal</option>
                  <option value="500">Medium</option>
                  <option value="bold">Bold</option>
                  <option value="700">Extra Bold</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loại màu</label>
                <select
                  value={tempFields.name?.colorType || 'solid'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, colorType: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="solid">Màu đơn (Solid)</option>
                  <option value="linear">Linear Gradient</option>
                  <option value="radial">Radial Gradient</option>
                </select>
              </div>
              {tempFields.name?.colorType === 'solid' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Màu chữ</label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={tempFields.name?.color || '#000000'}
                      onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, color: e.target.value } }))}
                      className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={tempFields.name?.color || '#000000'}
                      onChange={(e) => {
                        let value = e.target.value.toUpperCase();
                        if (!value.startsWith('#')) value = '#' + value;
                        value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                        if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                        value = value.substring(0, 7);
                        setTempFields((prev) => ({ ...prev, name: { ...prev.name, color: value } }));
                      }}
                      placeholder="#000000"
                      maxLength={7}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono uppercase"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Màu 1</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={tempFields.name?.gradientColor1 || '#FFD700'}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, gradientColor1: e.target.value } }))}
                        className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={tempFields.name?.gradientColor1 || '#FFD700'}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          if (!value.startsWith('#')) value = '#' + value;
                          value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                          if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                          value = value.substring(0, 7);
                          setTempFields((prev) => ({ ...prev, name: { ...prev.name, gradientColor1: value } }));
                        }}
                        placeholder="#FFD700"
                        maxLength={7}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono uppercase"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Màu 2</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={tempFields.name?.gradientColor2 || '#FF6B6B'}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, gradientColor2: e.target.value } }))}
                        className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={tempFields.name?.gradientColor2 || '#FF6B6B'}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          if (!value.startsWith('#')) value = '#' + value;
                          value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                          if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                          value = value.substring(0, 7);
                          setTempFields((prev) => ({ ...prev, name: { ...prev.name, gradientColor2: value } }));
                        }}
                        placeholder="#FF6B6B"
                        maxLength={7}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono uppercase"
                      />
                    </div>
                  </div>
                  {tempFields.name?.colorType === 'linear' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Góc (Angle): {tempFields.name?.gradientAngle || 45}°
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={tempFields.name?.gradientAngle || 45}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, gradientAngle: Number(e.target.value) } }))}
                        className="w-full"
                      />
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Căn lề</label>
                <select
                  value={tempFields.name?.align || 'center'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, align: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="left">Trái</option>
                  <option value="center">Giữa</option>
                  <option value="right">Phải</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font chữ</label>
                <select
                  value={tempFields.name?.fontFamily || 'Arial'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, name: { ...prev.name, fontFamily: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Roboto">Roboto</option>
                </select>
              </div>
            </div>

            <div className="space-y-5">
              <h3 className="text-xl font-bold text-purple-700">Chức vụ</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cỡ chữ (px)</label>
                <input
                  type="number"
                  value={tempFields.title?.fontSize || 40}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, fontSize: Number(e.target.value) } }))}
                  min={16}
                  max={100}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Độ đậm</label>
                <select
                  value={tempFields.title?.fontWeight || 'normal'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, fontWeight: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="normal">Normal</option>
                  <option value="500">Medium</option>
                  <option value="bold">Bold</option>
                  <option value="700">Extra Bold</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loại màu</label>
                <select
                  value={tempFields.title?.colorType || 'solid'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, colorType: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="solid">Màu đơn (Solid)</option>
                  <option value="linear">Linear Gradient</option>
                  <option value="radial">Radial Gradient</option>
                </select>
              </div>
              {tempFields.title?.colorType === 'solid' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Màu chữ</label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      value={tempFields.title?.color || '#666666'}
                      onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, color: e.target.value } }))}
                      className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={tempFields.title?.color || '#666666'}
                      onChange={(e) => {
                        let value = e.target.value.toUpperCase();
                        if (!value.startsWith('#')) value = '#' + value;
                        value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                        if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                        value = value.substring(0, 7);
                        setTempFields((prev) => ({ ...prev, title: { ...prev.title, color: value } }));
                      }}
                      placeholder="#666666"
                      maxLength={7}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono uppercase"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Màu 1</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={tempFields.title?.gradientColor1 || '#FFD700'}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, gradientColor1: e.target.value } }))}
                        className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={tempFields.title?.gradientColor1 || '#FFD700'}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          if (!value.startsWith('#')) value = '#' + value;
                          value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                          if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                          value = value.substring(0, 7);
                          setTempFields((prev) => ({ ...prev, title: { ...prev.title, gradientColor1: value } }));
                        }}
                        placeholder="#FFD700"
                        maxLength={7}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono uppercase"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Màu 2</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={tempFields.title?.gradientColor2 || '#FF6B6B'}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, gradientColor2: e.target.value } }))}
                        className="w-16 h-12 border border-gray-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={tempFields.title?.gradientColor2 || '#FF6B6B'}
                        onChange={(e) => {
                          let value = e.target.value.toUpperCase();
                          if (!value.startsWith('#')) value = '#' + value;
                          value = '#' + value.substring(1).replace(/[^0-9A-F]/g, '');
                          if (value.length === 4) value = '#' + value[1] + value[1] + value[2] + value[2] + value[3] + value[3];
                          value = value.substring(0, 7);
                          setTempFields((prev) => ({ ...prev, title: { ...prev.title, gradientColor2: value } }));
                        }}
                        placeholder="#FF6B6B"
                        maxLength={7}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 font-mono uppercase"
                      />
                    </div>
                  </div>
                  {tempFields.title?.colorType === 'linear' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Góc (Angle): {tempFields.title?.gradientAngle || 45}°
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={tempFields.title?.gradientAngle || 45}
                        onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, gradientAngle: Number(e.target.value) } }))}
                        className="w-full"
                      />
                    </div>
                  )}
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Căn lề</label>
                <select
                  value={tempFields.title?.align || 'center'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, align: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="left">Trái</option>
                  <option value="center">Giữa</option>
                  <option value="right">Phải</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Font chữ</label>
                <select
                  value={tempFields.title?.fontFamily || 'Arial'}
                  onChange={(e) => setTempFields((prev) => ({ ...prev, title: { ...prev.title, fontFamily: e.target.value } }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Arial">Arial</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Roboto">Roboto</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="relative">
          <div
            ref={previewRef}
            className="relative border-4 border-gray-300 rounded-2xl overflow-hidden bg-white shadow-2xl mx-auto"
            style={{
              width: `${editingTemplate.width * previewScale}px`,
              height: `${editingTemplate.height * previewScale}px`,
              maxWidth: '100%',
              aspectRatio: `${editingTemplate.width / editingTemplate.height}`
            }}
          >
            <img
              src={editingTemplate.imageUrl}
              alt="Template background"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
              onLoad={(e) => {
                const container = e.target.parentElement;
                if (container) {
                  const scale = Math.min(
                    container.clientWidth / editingTemplate.width,
                    container.clientHeight / editingTemplate.height,
                    0.9
                  );
                  setPreviewScale(scale);
                }
              }}
            />

            {guides.vertical.map((guide, index) => (
              <div
                key={`v-${index}`}
                className="absolute top-0 bottom-0 w-0.5 pointer-events-none z-50"
                style={{
                  left: `${guide.position * previewScale}px`,
                  opacity: 0.8,
                  backgroundColor: guide.color === 'purple' ? '#a855f7' : guide.color === 'green' ? '#22c55e' : '#3b82f6'
                }}
              >
                <div
                  className="absolute top-2 left-1/2 -translate-x-1/2 text-white text-xs px-2 py-1 rounded whitespace-nowrap"
                  style={{
                    backgroundColor: guide.color === 'purple' ? '#a855f7' : guide.color === 'green' ? '#22c55e' : '#3b82f6'
                  }}
                >
                  {guide.type}
                </div>
              </div>
            ))}

            {guides.horizontal.map((guide, index) => (
              <div
                key={`h-${index}`}
                className="absolute left-0 right-0 h-0.5 pointer-events-none z-50"
                style={{
                  top: `${guide.position * previewScale}px`,
                  opacity: 0.8,
                  backgroundColor: guide.color === 'purple' ? '#a855f7' : guide.color === 'green' ? '#22c55e' : '#3b82f6'
                }}
              >
                <div
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs px-2 py-1 rounded whitespace-nowrap"
                  style={{
                    backgroundColor: guide.color === 'purple' ? '#a855f7' : guide.color === 'green' ? '#22c55e' : '#3b82f6'
                  }}
                >
                  {guide.type}
                </div>
              </div>
            ))}

            {tempFields.photo && (
              <div
                className="absolute"
                style={{
                  left: `${tempFields.photo.x * previewScale}px`,
                  top: `${tempFields.photo.y * previewScale}px`,
                  width: `${tempFields.photo.width * previewScale}px`,
                  height: `${tempFields.photo.height * previewScale}px`,
                  cursor: dragging?.type === 'photo' ? 'grabbing' : 'grab'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'photo', 'drag')}
              >
                <div
                  className="bg-blue-100 bg-opacity-50 border-2 border-blue-500 flex items-center justify-center text-blue-700 font-semibold overflow-hidden relative"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius:
                      tempFields.photo.shape === 'circle'
                        ? '50%'
                        : tempFields.photo.shape === 'rounded'
                        ? `${(tempFields.photo.borderRadius || 20) * previewScale}px`
                        : '0px'
                  }}
                >
                  Ảnh đại diện
                  <div
                    className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleMouseDown(e, 'photo', 'resize');
                    }}
                  />
                </div>
              </div>
            )}

            {tempFields.name && (
              <div
                className="absolute bg-white bg-opacity-70 rounded-xl shadow-lg select-none flex items-center justify-center"
                style={{
                  left: `${tempFields.name.x * previewScale}px`,
                  top: `${tempFields.name.y * previewScale}px`,
                  fontSize: `${(tempFields.name.fontSize || 60) * previewScale}px`,
                  fontWeight: tempFields.name.fontWeight || 'bold',
                  color: tempFields.name.colorType === 'solid' ? (tempFields.name.color || '#000000') : 'transparent',
                  background: tempFields.name.colorType === 'linear'
                    ? `linear-gradient(${tempFields.name.gradientAngle || 45}deg, ${tempFields.name.gradientColor1 || '#FFD700'}, ${tempFields.name.gradientColor2 || '#FF6B6B'})`
                    : tempFields.name.colorType === 'radial'
                    ? `radial-gradient(circle, ${tempFields.name.gradientColor1 || '#FFD700'}, ${tempFields.name.gradientColor2 || '#FF6B6B'})`
                    : 'transparent',
                  WebkitBackgroundClip: tempFields.name.colorType !== 'solid' ? 'text' : 'border-box',
                  WebkitTextFillColor: tempFields.name.colorType !== 'solid' ? 'transparent' : (tempFields.name.color || '#000000'),
                  backgroundClip: tempFields.name.colorType !== 'solid' ? 'text' : 'border-box',
                  fontFamily: tempFields.name.fontFamily || 'Arial',
                  textAlign: tempFields.name.align || 'center',
                  transform: 'translate(-50%, -50%)',
                  whiteSpace: 'nowrap',
                  cursor: dragging?.type === 'name' ? 'grabbing' : 'grab'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'name', 'drag')}
              >
                Họ và tên
              </div>
            )}

            {tempFields.title && (
              <div
                className="absolute bg-white bg-opacity-70 rounded-xl shadow-lg select-none flex items-center justify-center"
                style={{
                  left: `${tempFields.title.x * previewScale}px`,
                  top: `${tempFields.title.y * previewScale}px`,
                  fontSize: `${(tempFields.title.fontSize || 40) * previewScale}px`,
                  fontWeight: tempFields.title.fontWeight || 'normal',
                  color: tempFields.title.colorType === 'solid' ? (tempFields.title.color || '#666666') : 'transparent',
                  background: tempFields.title.colorType === 'linear'
                    ? `linear-gradient(${tempFields.title.gradientAngle || 45}deg, ${tempFields.title.gradientColor1 || '#FFD700'}, ${tempFields.title.gradientColor2 || '#FF6B6B'})`
                    : tempFields.title.colorType === 'radial'
                    ? `radial-gradient(circle, ${tempFields.title.gradientColor1 || '#FFD700'}, ${tempFields.title.gradientColor2 || '#FF6B6B'})`
                    : 'transparent',
                  WebkitBackgroundClip: tempFields.title.colorType !== 'solid' ? 'text' : 'border-box',
                  WebkitTextFillColor: tempFields.title.colorType !== 'solid' ? 'transparent' : (tempFields.title.color || '#666666'),
                  backgroundClip: tempFields.title.colorType !== 'solid' ? 'text' : 'border-box',
                  fontFamily: tempFields.title.fontFamily || 'Arial',
                  textAlign: tempFields.title.align || 'center',
                  transform: 'translate(-50%, -50%)',
                  whiteSpace: 'nowrap',
                  cursor: dragging?.type === 'title' ? 'grabbing' : 'grab'
                }}
                onMouseDown={(e) => handleMouseDown(e, 'title', 'drag')}
              >
                Chức vụ
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
          <p className="text-center text-gray-700 text-lg font-medium mb-3">
            💡 Hướng dẫn Smart Guides
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span><strong>Xanh dương:</strong> Căn với template</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              <span><strong>Tím:</strong> Căn với elements khác</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span><strong>Xanh lá:</strong> Snap sát cạnh</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;